
import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnInit, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatButtonModule } from "@angular/material/button";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MSAL_INSTANCE } from "@azure/msal-angular";
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, of } from 'rxjs';
import { debounceTime, filter, take, tap } from 'rxjs/operators';
import { CapabilitiesService } from 'src/app/shared/services/capabilities/capabilities.service';
import { DigiDocsStore } from '../../../+state/digi-docs.store';
import { DigitizationParameter } from '../../../models/digitization-parameter.model';
import { Attribute, Document } from "src/app/shared/models/document.models";
import { IPublicClientApplication } from "@azure/msal-browser";
import { MsalIdToken } from "src/app/shared/models/account.models";
import { Order } from "src/app/shared/models/order";
import { OrderRuleAudit } from "src/app/shared/models/order-rule-audit.model";
import { DocumentRule } from "src/app/shared/enums/documentRule";
import { ToastrService } from "ngx-toastr";

interface DocumentEditFormState
{
  documentName: any,
  documentType: any,
  actionType: any,
  paperSize: any
}

@UntilDestroy()
@Component({
    selector: 'app-document-edit-form',
    templateUrl: './document-edit-form.component.html',
    styleUrls: ['./document-edit-form.component.scss'],
    imports: [
    AsyncPipe,
    FormsModule,
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule
]
})
export class DocumentEditFormComponent implements OnInit, OnChanges {
  @Output() exitDocumentEditor = new EventEmitter<void>();
  @Output() updateReviewedByAttribute = new EventEmitter<Document>();
  @Output() selectDocument = new EventEmitter<number>();
  @Input() isOrderLocked: boolean;
  @Input() order: Order;
  @Input() documents: Document[] = [];
  @Input() ruleViolations: OrderRuleAudit;
  @Input() selectedDocumentId: number;

  docAttributes: Attribute[] = [];
  sortedDocuments: Document[] = [];
  currentUserName: any;
  docId: any;
  requiresReview = false;
  reviewedBy: any;
  canDigitize$: Observable<boolean> = of(false);

  documentForm: UntypedFormGroup;

  digitizationParameters$ = this.componentStore.digitizationParameters$;

  private formState: DocumentEditFormState;

  constructor(
    private readonly componentStore: DigiDocsStore,
    private readonly capabilitiesService: CapabilitiesService,
    private readonly toastr: ToastrService,
    @Inject(MSAL_INSTANCE) private readonly msalInstance: IPublicClientApplication
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.updateFormWithDigitizationParameters().subscribe();
    this.watchFormAndUpdateState().subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['order'] && this.order?.id) {
      this.canDigitize$ = this.capabilitiesService.userHasCapability('order', 'CanDigitizeDocuments', this.order.id);
    }
    if (changes['documents']) {
      if (changes['documents'].firstChange) {
        this.sortedDocuments = [...(this.documents ?? [])].sort((a, b) =>
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        );
      } else {
        this.sortedDocuments = this.sortedDocuments.map(sorted =>
          this.documents.find(d => d.id === sorted.id) ?? sorted
        );
      }
    }
    if (changes['ruleViolations'] && this.ruleViolations && this.docId) {
      this.checkIfNeedsReviewed();
    }
    if (changes['selectedDocumentId'] && this.selectedDocumentId) {
      this.refreshFromStore();
    }
  }

  private refreshFromStore(): void {
    if (!this.documentForm) return;
    this.digitizationParameters$
      .pipe(take(1))
      .subscribe(params => this.updateLocalState(params));
  }

  handleExitDocumentEditor() {
    this.exitDocumentEditor.emit();
  }

  onDocumentSelected(documentId: number): void {
    this.selectDocument.emit(documentId);
  }

  documentNeedsAttention(docId: number): boolean {
    return this.ruleViolations?.documentRules?.some(d => d.packageDocumentId === docId) ?? false;
  }

  getCurrentDocViolationMessage(): string {
    if (this.ruleViolations?.documentRules?.some(d => d.packageDocumentId === this.docId)) {
      const violations = this.ruleViolations.documentRules
        .filter(d => d.packageDocumentId === this.docId && d.errorText)
        .map(d => `${this.formatRuleName(d.ruleName)} - ${d.errorText}`);
      return violations.length ? violations.join(', ') : 'Needs attention';
    }
    return 'Needs attention';
  }

  private formatRuleName(ruleName: string): string {
    return ruleName.replace(/([A-Z])/g, ' $1').trim();
  }

  initForm() {
    this.documentForm = new UntypedFormGroup({
      documentName: new UntypedFormControl({value: null, disabled: this.isOrderLocked}),
      documentType: new UntypedFormControl({value: null, disabled: this.isOrderLocked}),
      actionType: new UntypedFormControl({value: null, disabled: true}),
      paperSize: new UntypedFormControl({value: null, disabled: this.isOrderLocked})
    });
  }

  setControlEnabledState(name: string, isEnabled: boolean): void {
    const ctrl = this.control(name);
    if (ctrl === null || ctrl === undefined) {
      return;
    }
    if (isEnabled)
    {
      ctrl.enable({onlySelf: true, emitEvent: false});
    }
    else
    {
      ctrl.disable({onlySelf: true, emitEvent: false});
    }
  }

  setDisabled(name: string): void {
    this.setControlEnabledState(name, false);
  }

  setEnabled(name: string): void {
    this.setControlEnabledState(name, true);
  }

  control(name: string): AbstractControl | null {
    return this.documentForm.get(name);
  }

  updateFormWithDigitizationParameters(): Observable<DigitizationParameter> {
    return this.digitizationParameters$.pipe(
      filter(digitizationParameters => !!digitizationParameters),
      tap(digitizationParameters => this.updateLocalState(digitizationParameters)),
      untilDestroyed(this)
    );
  }

  private updateLocalState(digitizationParameters: DigitizationParameter): void {
    this.docId = digitizationParameters.mySelectedDocument?.id ?? 0;
    this.docAttributes = digitizationParameters.mySelectedDocument?.attributes ?? [];
    this.reviewedBy = null;

    const reviewedByAttr = this.docAttributes.find(a => a.key === "REVIEWEDBY");
    if (reviewedByAttr) {
      this.reviewedBy = reviewedByAttr.value;
    }

    this.checkIfNeedsReviewed();

    this.formState = {
      documentType: digitizationParameters.selectedDocumentType,
      documentName: digitizationParameters.documentName,
      actionType: digitizationParameters.actionType,
      paperSize: digitizationParameters.selectedPaperSize
    };

    this.documentForm.get('documentType')?.setValue(this.formState.documentType);
    this.documentForm.get('documentName')?.setValue(this.formState.documentName);
    this.documentForm.get('actionType')?.setValue(this.formState.actionType);
    this.documentForm.get('paperSize')?.setValue(this.formState.paperSize);

    if ((digitizationParameters.mySelectedDocument?.id || 0) > 0 &&
       digitizationParameters.mySelectedDocument?.isDigitized)
    {
      this.setDisabled('paperSize');
    }
    else
    {
      this.setEnabled('paperSize');
    }
  }

  markAsReviewed(): void {
    const users = this.msalInstance.getAllAccounts();

    const userToken = users[0].idTokenClaims as MsalIdToken;

    this.currentUserName = `${userToken.given_name} ${userToken.family_name}`;

    this.digitizationParameters$.pipe(take(1)).subscribe(params => {
      const currentDoc = params.mySelectedDocument;
      const existingAttributes = currentDoc?.attributes ?? [];

      const attribute = new Attribute();
      attribute.key = "REVIEWEDBY";
      attribute.value = this.currentUserName;

      const updatedAttributes = [...existingAttributes, attribute];
      this.docAttributes = updatedAttributes;

      const document: Document = {
        ...currentDoc,
        attributes: updatedAttributes,
      } as Document;

      this.updateReviewedByAttribute.emit(document);

      this.componentStore.updateDigitizationParameters({
        mySelectedDocument: document
      } as any);

      this.checkIfNeedsReviewed();

      if (this.requiresReview === true) {
        this.toastr.error("Marked as reviewed failed.");
      } else {
        this.toastr.success("Successfully marked as reviewed.");
        this.reviewedBy = this.currentUserName;
      }
    });
  }

  checkIfNeedsReviewed() {
    this.requiresReview = false;
    if(this.ruleViolations?.documentRules?.some(rv => rv.packageDocumentId === this.docId 
        && rv.ruleName === DocumentRule.RequiresReview) 
        && this.docAttributes.every(a => a.key !== "REVIEWEDBY")) {
      this.requiresReview = true;
    }
  }

  watchFormAndUpdateState(): Observable<any> {
    return this.documentForm.valueChanges.pipe(
      debounceTime(300),
      tap(value => {
        this.formState = {
          ...this.formState,
          ...value
        };

        const digitizationUpdates: Partial<DigitizationParameter> = {
          selectedDocumentType: this.formState.documentType,
          documentName: this.formState.documentName,
          actionType: this.formState.actionType,
          selectedPaperSize: this.formState.paperSize
        };
        this.componentStore.updateDigitizationParameters(digitizationUpdates as any);
      }),
      untilDestroyed(this)
    );
  }
}
